import { STOREFRONTS } from '../data/storefronts.js';
import { CHART_SORT_KEYS } from './sortChartEntries.js';

export const CHART_TYPES = /** @type {const} */ (['top-free', 'top-paid', 'top-grossing']);
export const CHART_DIRS = /** @type {const} */ (['asc', 'desc']);

export const CHART_BROWSER_QUERY_KEYS = Object.freeze({
  country: 'chartCountry',
  type: 'chartType',
  sortKey: 'chartSort',
  sortDir: 'chartDir',
});

export const DEFAULT_CHART_BROWSER_STATE = Object.freeze({
  country: 'us',
  type: 'top-free',
  sortKey: 'rank',
  sortDir: 'asc',
});

const ALLOWED_COUNTRIES = STOREFRONTS.map((sf) => sf.code);

/**
 * @typedef {typeof DEFAULT_CHART_BROWSER_STATE} ChartBrowserUrlState
 */

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalized(value) {
  return String(value ?? '').trim().toLowerCase();
}

/**
 * @param {unknown} value
 * @param {readonly string[]} allowed
 * @param {string} fallback
 * @returns {string}
 */
function allowedValue(value, allowed, fallback) {
  const next = normalized(value);
  return allowed.includes(next) ? next : fallback;
}

/**
 * @param {URLSearchParams|string|URL|null|undefined} input
 * @returns {URLSearchParams}
 */
function toSearchParams(input) {
  if (input instanceof URLSearchParams) return new URLSearchParams(input);
  if (input instanceof URL) return new URLSearchParams(input.search);
  if (typeof input === 'string') return new URLSearchParams(input.startsWith('?') ? input.slice(1) : input);
  return new URLSearchParams();
}

/**
 * Parse Chart Browser state from query params. Unknown or unsupported values
 * fall back independently, so one bad param never discards the rest.
 *
 * @param {URLSearchParams|string|URL|null|undefined} input
 * @returns {ChartBrowserUrlState}
 */
export function parseChartBrowserState(input) {
  const params = toSearchParams(input);
  return {
    country: allowedValue(
      params.get(CHART_BROWSER_QUERY_KEYS.country),
      ALLOWED_COUNTRIES,
      DEFAULT_CHART_BROWSER_STATE.country,
    ),
    type: allowedValue(
      params.get(CHART_BROWSER_QUERY_KEYS.type),
      CHART_TYPES,
      DEFAULT_CHART_BROWSER_STATE.type,
    ),
    sortKey: allowedValue(
      params.get(CHART_BROWSER_QUERY_KEYS.sortKey),
      CHART_SORT_KEYS,
      DEFAULT_CHART_BROWSER_STATE.sortKey,
    ),
    sortDir: allowedValue(
      params.get(CHART_BROWSER_QUERY_KEYS.sortDir),
      CHART_DIRS,
      DEFAULT_CHART_BROWSER_STATE.sortDir,
    ),
  };
}

/**
 * Serialize Chart Browser state into a fresh URLSearchParams, preserving any
 * unrelated existing params.
 *
 * @param {Partial<ChartBrowserUrlState>|null|undefined} state
 * @param {URLSearchParams|string|URL|null|undefined} [baseParams]
 * @returns {URLSearchParams}
 */
export function serializeChartBrowserState(state, baseParams) {
  const params = toSearchParams(baseParams);
  const normalizedState = {
    country: allowedValue(state?.country, ALLOWED_COUNTRIES, DEFAULT_CHART_BROWSER_STATE.country),
    type: allowedValue(state?.type, CHART_TYPES, DEFAULT_CHART_BROWSER_STATE.type),
    sortKey: allowedValue(state?.sortKey, CHART_SORT_KEYS, DEFAULT_CHART_BROWSER_STATE.sortKey),
    sortDir: allowedValue(state?.sortDir, CHART_DIRS, DEFAULT_CHART_BROWSER_STATE.sortDir),
  };

  params.set(CHART_BROWSER_QUERY_KEYS.country, normalizedState.country);
  params.set(CHART_BROWSER_QUERY_KEYS.type, normalizedState.type);
  params.set(CHART_BROWSER_QUERY_KEYS.sortKey, normalizedState.sortKey);
  params.set(CHART_BROWSER_QUERY_KEYS.sortDir, normalizedState.sortDir);
  return params;
}
